import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Link,
  Divider,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { CONTEXTS, CONTROLLERS } from 'figshelf-modules';
import SocialButtons from '../components/SocialButtons';
import AuthPageLayout from '../components/AuthPageLayout';
import { useAuthModal } from '../contexts/AuthModalContext';
import { constants } from 'figshelf-modules';


const MOBILE_APK_URL = constants.MOBILE_APK_URL;

const Login = ({ embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useContext(CONTEXTS.AuthContext);
  const api = useContext(CONTEXTS.APIContext);
  const authController = new CONTROLLERS.AuthController(api);
  const authModal = useAuthModal();

  const errorFieldSx = {
    '& .MuiFormHelperText-root': { ml: 0 },
    '& .MuiFormHelperText-root.Mui-error': { fontWeight: 500 },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: 'error.main',
      borderWidth: 2,
    },
  };

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [identifierError, setIdentifierError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Load saved credentials if remember me was checked
  useEffect(() => {
    const savedIdentifier = localStorage.getItem('saved_identifier');
    const savedRememberMe = localStorage.getItem('remember_me');
    
    if (savedIdentifier && savedRememberMe === 'true') {
      setIdentifier(savedIdentifier);
      setRememberMe(true);
    }
  }, []);

  const validateForm = () => {
    let isValid = true;
    setIdentifierError('');
    setPasswordError('');
    setError('');

    if (!identifier.trim()) {
      setIdentifierError('Email, username, or phone number is required');
      isValid = false;
    }

    if (!password.trim()) {
      setPasswordError('Password is required');
      isValid = false;
    }

    return isValid;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    try {
      // Attempt login
      const res = await authController.login(identifier, password);

      // Handle 2FA if required
      if (res.data.requires2FA) {
        navigate('/verify-2fa', {
          state: {
            tempToken: res.data.tempToken,
            identifier,
          },
        });
        return;
      }

      // Save credentials if remember me is checked
      if (rememberMe) {
        localStorage.setItem('saved_identifier', identifier);
        localStorage.setItem('remember_me', 'true');
      } else {
        localStorage.removeItem('saved_identifier');
        localStorage.removeItem('remember_me');
      }

      // Sign in with tokens
      const success = await auth.signIn(res.data.accessToken, res.data.refreshToken);

      if (success) {
        const from = location.state?.from?.pathname || '/';
        navigate(from, { replace: true });
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.response?.status === 401) {
        setPasswordError('Invalid password');
        setPassword('');
      } else {
        setError(err.message || 'Login failed. Please check your credentials.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  const isFormValid = identifier.trim() !== '' && password.trim() !== '';

  const content = (
    <>
      {/* Logo */}
      {!embedded && (
        <Box sx={{ textAlign: 'center', mb: 2, mt: 1 }}>
          <img
            src="/assets/logos/logo_1.png"
            alt="FigShelf Logo"
            style={{ height: 56 }}
          />
        </Box>
      )}

          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 2.5 }}>
            <Typography
              variant="h5"
              fontWeight="bold"
              gutterBottom
              sx={{
                color: 'primary.main',
              }}
            >
              Welcome Back
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to continue to FigShelf
            </Typography>
          </Box>

          {/* Get the FigShelf Mobile App */}
          <Box
            sx={{
              p: 2.5,
              backgroundColor: 'background.paper',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              mb: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <img
                src="/assets/adaptive-icon.png"
                alt="FigShelf"
                style={{ height: 48, width: 48, objectFit: 'cover', borderRadius: 12 }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Get the FigShelf Mobile App
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Take your collection on the go with our mobile app
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  if (MOBILE_APK_URL) {
                    window.open(MOBILE_APK_URL, '_blank');
                  }
                }}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  minWidth: 'auto',
                  px: 2,
                }}
              >
                Download
              </Button>
            </Box>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <Box component="form" noValidate>
            <TextField
              label="Email, Username, or Phone"
              variant="outlined"
              fullWidth
              autoComplete="username"
              value={identifier}
              onChange={(e) => {
                setIdentifier(e.target.value);
                setIdentifierError('');
              }}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              error={!!identifierError}
              helperText={identifierError}
              sx={{ mb: 2, ...errorFieldSx }}
            />

            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordError('');
              }}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              error={!!passwordError}
              helperText={passwordError}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2, ...errorFieldSx }}
            />

            {/* Remember Me & Forgot Password */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    color="primary"
                  />
                }
                label="Remember me"
              />
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/forgot-password')}
                sx={{ textDecoration: 'none' }}
              >
                Forgot Password?
              </Link>
            </Box>

            {/* Login Button */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleLogin}
              disabled={!isFormValid || isLoading}
              sx={{
                py: 1.5,
                mb: 2,
                background: 'primary.main',
                '&:hover': {
                  background: 'primary.dark',
                },
                '&:disabled': {
                  background: 'action.disabledBackground',
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>

            {/* Divider */}
            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>

            {/* Social Login Buttons */}
            <Box sx={{ mb: 3 }}>
              <SocialButtons />
            </Box>

            {/* Sign Up Link */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => {
                    if (embedded) {
                      authModal.openSignup();
                      return;
                    }
                    navigate('/register');
                  }}
                  sx={{
                    fontWeight: 'bold',
                    textDecoration: 'none',
                    color: 'primary.main',
                  }}
                >
                  Sign Up
                </Link>
              </Typography>
            </Box>
          </Box>
    </>
  );

  if (embedded) {
    return <Box sx={{ width: '100%' }}>{content}</Box>;
  }

  return (
    <AuthPageLayout paperMaxWidth={460} showDesktopHeader={true}>
      {content}
    </AuthPageLayout>
  );
};

export default Login;